import React, { useState, useEffect } from 'react';
import { api } from '../config/api';

interface Lease {
  id: number;
  tenant_names: string;
  dependents: string;
  rent_monthly: number;
  security_deposit: number;
  lease_start: string;
  lease_end: string;
  landscaping_by: string | null;
  water_amount: number;
  internet_included: number;
  pet_deposit: string | null;
  source_file: string;
  address: string;
  is_month_to_month: boolean;
  days_remaining: number;
  pdf_url: string | null;
}

interface LeaseData {
  count: number;
  total_rent: number;
  m2m_count: number;
  expiring_60: number;
  leases: Lease[];
}

interface LeaseVersion {
  id: number;
  tenant: string;
  version: string;
  status: string;
  sections: number;
  missing_clauses: string[];
}

interface VersionData {
  total: number;
  current_count: number;
  outdated_count: number;
  no_pdf_count: number;
  leases: LeaseVersion[];
}

const fmt = (n: number) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(n || 0);

const fmtDate = (d: string | null) => {
  if (!d) return '-';
  const dt = new Date(d + 'T00:00:00');
  return dt.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

export const CurrentLeases: React.FC = () => {
  const [data, setData] = useState<LeaseData | null>(null);
  const [versions, setVersions] = useState<VersionData | null>(null);
  const [previewPdf, setPreviewPdf] = useState<{ url: string; name: string } | null>(null);

  useEffect(() => {
    api.get('/lease-qa/leases').then(res => setData(res.data)).catch(() => {});
    api.get('/lease-qa/lease-versions').then(res => setVersions(res.data)).catch(() => {});
  }, []);

  // Build version lookup by lease id
  const versionMap = new Map<number, LeaseVersion>();
  if (versions) {
    for (const v of versions.leases) {
      versionMap.set(v.id, v);
    }
  }

  if (!data) return <div className="text-gray-500 font-mono p-8">Loading leases...</div>;

  const monthlyColor = (a: number) => a >= 3000 ? 'text-red-400' : a >= 2000 ? 'text-yellow-300' : 'text-green-400';

  return (
    <div className="space-y-5 pb-8">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-white font-mono flex items-center gap-3">
          <span className="text-cyan-400">~/</span>current-leases
        </h1>
        <p className="text-gray-500 text-sm mt-1 font-mono">
          Active rental agreements &mdash; click any row to view the lease PDF
        </p>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="bg-gray-800/40 border border-gray-700/30 rounded-lg p-4">
          <div className="text-gray-500 text-[10px] font-mono uppercase tracking-wider">Active Leases</div>
          <div className="text-xl font-bold font-mono mt-1 text-white">{data.count}</div>
        </div>
        <div className="bg-gray-800/40 border border-gray-700/30 rounded-lg p-4">
          <div className="text-gray-500 text-[10px] font-mono uppercase tracking-wider">Monthly Rent</div>
          <div className="text-xl font-bold font-mono mt-1 text-yellow-400">{fmt(data.total_rent)}</div>
          <div className="text-gray-600 text-[10px] font-mono">{fmt(data.total_rent * 12)}/yr</div>
        </div>
        <div className="bg-gray-800/40 border border-gray-700/30 rounded-lg p-4">
          <div className="text-gray-500 text-[10px] font-mono uppercase tracking-wider">Month-to-Month</div>
          <div className={`text-xl font-bold font-mono mt-1 ${data.m2m_count > 0 ? 'text-yellow-400' : 'text-green-400'}`}>{data.m2m_count}</div>
          <div className="text-gray-600 text-[10px] font-mono">lease term expired</div>
        </div>
        <div className="bg-gray-800/40 border border-gray-700/30 rounded-lg p-4">
          <div className="text-gray-500 text-[10px] font-mono uppercase tracking-wider">Expiring in 60 Days</div>
          <div className={`text-xl font-bold font-mono mt-1 ${data.expiring_60 > 0 ? 'text-orange-400' : 'text-green-400'}`}>{data.expiring_60}</div>
          <div className="text-gray-600 text-[10px] font-mono">need renewal discussion</div>
        </div>
        {versions && (
          <div className="bg-gray-800/40 border border-gray-700/30 rounded-lg p-4">
            <div className="text-gray-500 text-[10px] font-mono uppercase tracking-wider">Lease Version</div>
            <div className="flex items-baseline gap-2 mt-1">
              <span className="text-green-400 font-bold font-mono">{versions.current_count}</span>
              <span className="text-gray-600 text-xs font-mono">current</span>
              <span className="text-red-400 font-bold font-mono ml-1">{versions.outdated_count}</span>
              <span className="text-gray-600 text-xs font-mono">outdated</span>
            </div>
            <div className="text-gray-600 text-[10px] font-mono">{versions.no_pdf_count} missing PDFs</div>
          </div>
        )}
      </div>

      {/* Outdated Lease Warning */}
      {versions && versions.outdated_count > 0 && (
        <div className="bg-red-900/10 border border-red-700/20 rounded-lg p-4">
          <div className="text-red-400 text-xs font-mono font-bold uppercase tracking-wider mb-1">
            {versions.outdated_count} Tenants on Outdated Lease Versions
          </div>
          <div className="text-gray-500 text-xs font-mono mb-3">
            Missing clauses: Animal Welfare, Criminal Activity Termination, 72hr Abandonment, Water Leak Emergency Entry
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-1">
            {versions.leases.filter(v => v.status !== 'current').map((v, i) => (
              <div key={i} className="flex items-center gap-2 py-1">
                <span className={`w-2 h-2 rounded-full flex-shrink-0 ${
                  v.status === 'very_old' ? 'bg-red-500' : v.status === 'missing' ? 'bg-gray-500' : 'bg-orange-400'
                }`} />
                <span className="text-gray-300 text-xs font-mono flex-1">{v.tenant}</span>
                <span className={`text-[10px] font-mono font-bold px-1.5 py-0.5 rounded ${
                  v.status === 'very_old' ? 'bg-red-500/20 text-red-400' :
                  v.status === 'missing' ? 'bg-gray-500/20 text-gray-400' :
                  'bg-orange-500/20 text-orange-400'
                }`}>
                  {v.version}
                </span>
                <span className="text-gray-600 text-[10px] font-mono">{v.missing_clauses.length} missing</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Legend */}
      <div className="flex flex-wrap items-center gap-5 px-3 py-2 bg-gray-800/20 border border-gray-700/20 rounded-lg text-xs font-mono text-gray-400">
        <span className="text-gray-500 uppercase tracking-wider font-bold text-[10px]">Legend:</span>
        <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full bg-green-400 inline-block" /> Active &mdash; within lease term</span>
        <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full bg-yellow-400 inline-block" /> <span className="text-yellow-400 font-semibold">M2M</span> &mdash; Month-to-Month (lease expired, tenant still renting)</span>
        <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full bg-orange-400 inline-block" /> <span className="text-orange-400 font-semibold">##d</span> &mdash; days until lease expires (60 or fewer)</span>
        <span className="flex items-center gap-1.5"><span className="text-green-400">$</span> &lt;$2K &nbsp; <span className="text-yellow-300">$</span> $2K-$3K &nbsp; <span className="text-red-400">$</span> $3K+</span>
      </div>

      {/* Lease Table */}
      <div className="overflow-x-auto">
        <table className="w-full text-sm font-mono">
          <thead>
            <tr className="text-gray-500 text-[10px] uppercase tracking-wider border-b border-gray-700">
              <th className="text-center py-2 px-2 w-8"></th>
              <th className="text-left py-2 px-2">Tenant</th>
              <th className="text-left py-2 px-2">Property</th>
              <th className="text-right py-2 px-2">Rent</th>
              <th className="text-right py-2 px-2">Deposit</th>
              <th className="text-left py-2 px-2">Lease Start</th>
              <th className="text-left py-2 px-2">Lease End</th>
              <th className="text-center py-2 px-2">Status</th>
              <th className="text-center py-2 px-2">Cox</th>
              <th className="text-center py-2 px-2">Version</th>
              <th className="text-left py-2 px-2">Landscaping</th>
            </tr>
          </thead>
          <tbody>
            {data.leases.map(lease => {
              const isM2M = lease.is_month_to_month;
              const isExpiringSoon = !isM2M && lease.days_remaining > 0 && lease.days_remaining <= 60;
              return (
                <tr
                  key={lease.id}
                  onClick={() => lease.pdf_url && setPreviewPdf({ url: lease.pdf_url, name: `${lease.tenant_names} - ${lease.address}` })}
                  className={`border-b border-gray-800/30 transition-colors cursor-pointer ${
                    isM2M ? 'hover:bg-yellow-900/10 bg-yellow-900/5' : isExpiringSoon ? 'hover:bg-orange-900/10' : 'hover:bg-gray-800/20'
                  }`}
                >
                  {/* Status dot */}
                  <td className="py-2.5 px-2 text-center">
                    <span className={`inline-block w-2.5 h-2.5 rounded-full ${
                      isM2M ? 'bg-yellow-400' : isExpiringSoon ? 'bg-orange-400' : 'bg-green-400'
                    }`} />
                  </td>
                  {/* Tenant */}
                  <td className="py-2.5 px-2 text-gray-200 font-semibold">{lease.tenant_names}</td>
                  {/* Address */}
                  <td className="py-2.5 px-2 text-gray-400 text-xs max-w-[250px] truncate">{lease.address}</td>
                  {/* Rent */}
                  <td className={`py-2.5 px-2 text-right font-semibold ${monthlyColor(lease.rent_monthly)}`}>
                    {fmt(lease.rent_monthly)}
                  </td>
                  {/* Deposit */}
                  <td className="py-2.5 px-2 text-right text-gray-500">
                    {lease.security_deposit > 0 ? fmt(lease.security_deposit) : '$0'}
                  </td>
                  {/* Start */}
                  <td className="py-2.5 px-2 text-gray-400 text-xs">{fmtDate(lease.lease_start)}</td>
                  {/* End */}
                  <td className="py-2.5 px-2 text-gray-400 text-xs">{fmtDate(lease.lease_end)}</td>
                  {/* Status */}
                  <td className="py-2.5 px-2 text-center">
                    {isM2M ? (
                      <span className="text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded border bg-yellow-500/20 text-yellow-400 border-yellow-600/30">
                        M2M
                      </span>
                    ) : isExpiringSoon ? (
                      <span className="text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded border bg-orange-500/20 text-orange-400 border-orange-600/30">
                        {lease.days_remaining}d
                      </span>
                    ) : (
                      <span className="text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded border bg-green-500/20 text-green-400 border-green-600/30">
                        {lease.days_remaining}d
                      </span>
                    )}
                  </td>
                  {/* Cox Internet */}
                  <td className="py-2.5 px-2 text-center">
                    {lease.internet_included ? (
                      <span className="text-cyan-400 text-xs font-semibold">Yes</span>
                    ) : (
                      <span className="text-gray-600 text-xs">No</span>
                    )}
                  </td>
                  {/* Lease Version */}
                  <td className="py-2.5 px-2 text-center">
                    {(() => {
                      const v = versionMap.get(lease.id);
                      if (!v) return <span className="text-gray-600 text-xs">-</span>;
                      const cls = v.status === 'current' ? 'bg-green-500/20 text-green-400 border-green-600/30' :
                        v.status === 'very_old' ? 'bg-red-500/20 text-red-400 border-red-600/30' :
                        v.status === 'missing' ? 'bg-gray-500/20 text-gray-400 border-gray-600/30' :
                        'bg-orange-500/20 text-orange-400 border-orange-600/30';
                      return (
                        <span className={`text-[9px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded border ${cls}`}
                          title={v.missing_clauses.length > 0 ? `Missing: ${v.missing_clauses.join(', ')}` : 'Up to date'}>
                          {v.version}
                        </span>
                      );
                    })()}
                  </td>
                  {/* Landscaping */}
                  <td className="py-2.5 px-2 text-gray-500 text-xs">{lease.landscaping_by || '-'}</td>
                </tr>
              );
            })}
            {/* Totals */}
            <tr className="border-t-2 border-yellow-600/30 bg-yellow-900/10">
              <td className="py-2 px-2" />
              <td className="py-2 px-2 text-yellow-400 font-bold" colSpan={2}>TOTAL ({data.count} leases)</td>
              <td className="py-2 px-2 text-right text-yellow-400 font-bold">{fmt(data.total_rent)}</td>
              <td className="py-2 px-2 text-right text-gray-500">{fmt(data.leases.reduce((s, l) => s + (l.security_deposit || 0), 0))}</td>
              <td colSpan={6} />
            </tr>
          </tbody>
        </table>
      </div>

      {/* PDF Preview Modal */}
      {previewPdf && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4" onClick={() => setPreviewPdf(null)}>
          <div className="bg-gray-900 border border-gray-700 rounded-xl w-full max-w-5xl h-[85vh] flex flex-col" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-700">
              <span className="text-white font-mono text-sm truncate flex-1 mr-4">{previewPdf.name}</span>
              <div className="flex gap-2 flex-shrink-0">
                <a href={previewPdf.url} download className="text-xs font-mono text-cyan-400 bg-cyan-900/20 px-3 py-1 rounded border border-cyan-700/30 hover:bg-cyan-800/30">Download</a>
                <button onClick={() => setPreviewPdf(null)} className="text-gray-400 hover:text-white text-lg px-2">&times;</button>
              </div>
            </div>
            <iframe src={previewPdf.url} className="flex-1 w-full bg-white rounded-b-xl" title="Lease PDF" />
          </div>
        </div>
      )}
    </div>
  );
};

export default CurrentLeases;
